import type { ReactNode } from 'react';
import { Tooltip } from '../Tooltip';

/**
 * One node on a `<TreeChart>` — a card, not a table row.
 *
 * Shared rather than per-feature so the read-only chart and the surface that edits it are
 * the same picture: the only difference between them is whether `actions` is passed.
 *
 * **The card carries the record's name and nothing else.** A chart is read for its shape, and
 * every further value competes with the structure while widening the cards and pushing the
 * levels apart. The rank is the column the card is drawn in, and the column is labelled — the
 * code, the order and the description are on the Edit form.
 */
export function TreeNodeCard({
  name,
  status,
  actions,
  muted = false,
  sub = false,
}: {
  name: string;
  /** A `<StatusText>` pill, for a state worth marking. A live node needs none. */
  status?: ReactNode;
  /** The node's kebab. Absent on a read-only surface, which is what makes it read-only. */
  actions?: ReactNode;
  /** Tints the card back for a node that is no longer live. */
  muted?: boolean;
  /**
   * A smaller card, for a node in a **sub-tier** of its lane — one whose parent holds the
   * same rank.
   *
   * Only the height and the type shrink here. The card’s LENGTH along the chart’s own axis
   * belongs to the lane box, which already takes the sub-tier’s inset out of it — so setting
   * a width here would fight the layout rather than follow it.
   */
  sub?: boolean;
}) {
  return (
    <div
      className={[
        // Fills the lane box `<TreeChart>` puts it in rather than sizing to its own content:
        // a card as wide as its name would put the next lane at a different offset on every
        // branch, and the columns would stop lining up across them.
        'flex h-full w-full items-center rounded-xl border border-slate-400 shadow-sm',
        sub ? 'gap-1.5 py-0.5 pl-2' : 'gap-2 py-1 pl-3',
        // No trailing padding when the kebab is there: the button's own box supplies it.
        actions ? 'pr-1' : sub ? 'pr-2' : 'pr-3',
        /*
          A light slate ground, so a card reads as a solid object on the panel rather than as
          an outline drawn on it. A deleted one goes a step further back, or the two states
          would be told apart by the marker alone.
        */
        muted ? 'bg-slate-100' : 'bg-slate-50',
      ].join(' ')}
    >
      {/*
        Two lines, then an ellipsis.

        One line clipped a name like "Division Under the Office of the Director" so early that
        neighbouring units read as identical; two gives a long name enough room to distinguish
        itself while still bounding the card, which is what keeps a generation's cards a
        consistent width. Deliberately not a table's one-value-per-cell rule — a node card is
        the record's identity and has nothing else to give the name room.

        `min-w-0` is what lets the clamp work inside a flex row: without it the span keeps its
        content width and pushes the status and the kebab off the card's edge. The full name
        stays reachable as the title, because a clipped one is the whole identity.
      */}
      <Tooltip label={name} block>
      <span
        className={[
          'line-clamp-2 min-w-0 flex-1 leading-snug text-slate-600',
          sub ? 'text-[11px]' : 'text-xs',
        ].join(' ')}
      >
        {name}
      </span>
      </Tooltip>

      {status ? <span className="shrink-0">{status}</span> : null}

      {actions ? <span className="shrink-0">{actions}</span> : null}
    </div>
  );
}
